import cn from 'classnames';
import { useRef, useState, useContext } from 'react';
import { CSSTransition } from 'react-transition-group';

import DiscountTag from 'components/common/DiscountTag';
import Heading from 'components/common/Heading';
import StrikethroughSkeleton from 'components/common/StrikethroughSkeleton';
import Text from 'components/common/Text';
import Tooltip from 'components/common/Tooltip';
import CheckCircle from 'components/icons/accent/CheckCircle';
import IdMeBilling from 'components/icons/IdMeBilling';
import CaretDown from 'components/icons/navigation/CaretDown';
import CaretRight from 'components/icons/navigation/CaretRight';
import { PickYourMealsContext } from 'components/signUpFunnel/Contexts';
import FreeForLifeBanner from 'components/signUpFunnel/FreeForLifeBanner';
import MaxDiscountMessage from 'components/signUpFunnel/shared/MaxDiscountMessage';
import { HOME_CHEF, TEMPO } from 'context/BrandContext';
import { getServingsForDisplay } from 'helpers/servings';
import { basketCutoffDate, basketCutoffTime } from 'helpers/timeFormat';
import useBrand from 'hooks/useBrand';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import { hideIdMe } from 'lib/constants/idme';

const OrderSummary = ({ box, user, setShowSlideout, voucher }) => {
  const { isPickYourMeals } = useContext(PickYourMealsContext);
  const [showItemizedCheckout, setShowItemizedCheckout] = useState(false);
  const mealPlanSlug = useFunnelMealPlan();
  const isHomeChef = useBrand(HOME_CHEF);
  const isTempo = useBrand(TEMPO);
  const transitionRef = useRef();
  const { isLargeDesktop: isDesktopUser } = useScreenSizeConstants();

  const displayServings = getServingsForDisplay(user?.servings, mealPlanSlug);

  const hasPromoApplied = () => {
    return (
      voucher ||
      user?.isAmex ||
      user?.isUsaa ||
      (user?.idMeData?.idMeUser && user?.signupVoucher) ||
      box?.promoApplied
    );
  };

  const PromoCodesAndGiftCards = () => (
    <section
      className={cn(
        'mt-4 rounded-2xl border border-solid bg-white-100 px-4 py-3 bp3:sticky bp3:top-10 bp3:mt-4',
        isTempo ? 'border-charcoal-60' : 'border-cream-120'
      )}
    >
      <div className="flex justify-between">
        <button
          type="button"
          onClick={() => setShowSlideout(true)}
          className="items-left flex w-full cursor-pointer justify-between border-none bg-transparent"
        >
          <span className="flex flex-col items-start">
            <Text
              htmlElement="span"
              brandStylesOverrides="textMd"
              className={cn('font-medium', {
                'text-charcoal-100': !isTempo,
                'text-black-100': isTempo,
              })}
            >
              Promo Codes & Gift Cards
            </Text>
            {!hideIdMe(voucher, user) && (
              <span className="mt-1 flex items-center">
                <IdMeBilling />
                <Text
                  brandStylesOverrides="textSm pl-1 pb-0.5"
                  className={cn('font-medium', {
                    'text-green-100': !isTempo,
                    'text-navy-100': isTempo,
                  })}
                >
                  Discounts Available!
                </Text>
              </span>
            )}
          </span>

          <span className="flex items-center gap-2">
            {hasPromoApplied() && <CheckCircle className="h-6 w-6" />}
            <span className="flex items-center">
              <CaretRight className="h-6 w-6" />
            </span>
          </span>
        </button>
      </div>
    </section>
  );

  const AdditionalOrderInformation = () => {
    const unavailableServingsTotal = user?.availability?.unavailableServingsTotal;
    const cutoffDate = basketCutoffDate(box?.editingCutoff);
    const cutoffTime = basketCutoffTime(box?.editingCutoff);

    if (isPickYourMeals && unavailableServingsTotal > 0) {
      return (
        <div className="mt-3 rounded border border-solid border-charcoal-60 bg-charcoal-50 p-2">
          <Text brandStylesOverrides="textSm text-secondary">
            Heads up, {unavailableServingsTotal} of your meals{' '}
            {unavailableServingsTotal > 1 ? 'are' : 'is'} unavailable. We have updated your box
            price. You can signup and make changes until {cutoffTime} on {cutoffDate}. Skip, pause
            or cancel anytime.{' '}
          </Text>
        </div>
      );
    }

    return (
      <div
        className={cn(
          'mt-4 rounded-lg border border-solid p-2 text-center',
          isTempo ? 'border-tempo-gray-120 bg-tempo-gray-100' : 'border-cream-120 bg-cream-110'
        )}
      >
        <Text brandStylesOverrides="textSm">You can skip, pause, or cancel any time.</Text>
      </div>
    );
  };

  const ShippingAndTaxSummary = () => {
    return (
      <>
        <div className="flex pb-2">
          <Text htmlElement="dt" brandStylesOverrides="textMd" className="flex-1 font-normal">
            Shipping
          </Text>
          {box?.shippingPromoApplied ? (
            <Text htmlElement="dd" className="flex">
              <Text className="px-2 line-through">
                {box?.promo.shippingPromo.estimatedShippingFormatted}
              </Text>
              <Text>{box?.promo.shippingPromo.shippingPromoText}</Text>
            </Text>
          ) : (
            <Text htmlElement="dd">
              <Text>{box?.shippingFormatted} </Text>
            </Text>
          )}
        </div>
        <div className="flex pb-2">
          <Text brandStylesOverrides="textMd" className="flex-1 font-normal" htmlElement="dt">
            Tax
          </Text>
          <Text htmlElement="dd">{box?.taxFormatted}</Text>
        </div>
      </>
    );
  };

  const FullPriceOrWithSmsDiscount = () => {
    return (
      <>
        {user?.smsDiscount && box?.smsIncentivePromo ? (
          <Text
            htmlElement="dd"
            className="flex items-center whitespace-nowrap"
            brandStylesOverrides="text-secondary"
          >
            {isDesktopUser && (
              <DiscountTag discountText="5% off" brandStylesOverrides="text-secondary" />
            )}
            <StrikethroughSkeleton
              strikethroughText={box?.subtotalFormatted}
              textDisplayed={box?.smsIncentivePromo.subtotalFormatted}
            />
          </Text>
        ) : (
          <Text htmlElement="span">{box?.subtotalFormatted}</Text>
        )}
      </>
    );
  };

  const FullPriceOrVoucherSummary = () => {
    return (
      <>
        <div className={cn('flex pb-2', { 'mb-2': box?.promo?.maxDiscountMessage })}>
          <Text htmlElement="dt" className="flex-1 font-normal" brandStylesOverrides="textMd">
            Subtotal
          </Text>
          {box?.promoApplied && box?.subtotalFormatted !== box?.promo.subtotalFormatted ? (
            <Text
              htmlElement="dd"
              className="flex items-center whitespace-nowrap"
              brandStylesOverrides="text-secondary"
            >
              {user?.smsDiscount && (
                <DiscountTag discountText="5% off" brandStylesOverrides="text-secondary" />
              )}
              {isDesktopUser && (
                <DiscountTag
                  discountText={box.promo.displayedDiscount}
                  brandStylesOverrides="text-secondary"
                />
              )}
              <StrikethroughSkeleton
                strikethroughText={box?.subtotalFormatted}
                textDisplayed={box?.promo.subtotalFormatted}
              />
            </Text>
          ) : (
            <FullPriceOrWithSmsDiscount />
          )}
        </div>
        <MaxDiscountMessage maxDiscountMessage={box?.promo?.maxDiscountMessage} />
        <ShippingAndTaxSummary />
      </>
    );
  };

  const GiftCardSummary = () => {
    return (
      <>
        <div className="flex">
          <Text htmlElement="dt" className="flex-1">
            <Text brandStylesOverrides="textMd">Subtotal</Text>
          </Text>
          <Text htmlElement="dd">
            <Text>{box?.subtotalFormatted}</Text>
          </Text>
        </div>
        <ShippingAndTaxSummary />
        <hr className="rule bg-charcoal-60" />
        <div className="mt-2 flex">
          <Text htmlElement="dt" className="flex-1">
            <Text brandStylesOverrides="textMd">Gift Card</Text>
          </Text>
          <Text htmlElement="dd">
            <Text>{`-${box?.creditAppliedFormatted}`}</Text>
          </Text>
        </div>
      </>
    );
  };

  return (
    <div className="mx-6 pt-6 bp3:mx-0 bp3:w-full bp3:max-w-25 bp3:flex-none bp3:pt-0">
      <section
        className={cn(
          'rounded-2xl border border-solid bg-white-100 px-4 py-4 bp3:sticky bp3:top-10',
          isTempo ? 'border-charcoal-60' : 'border-cream-120'
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex grow">
            <Heading
              brandStylesOverrides={isDesktopUser ? 'h4' : 'textMd'}
              className={isDesktopUser ? 'mb-4' : 'pt-1'}
            >
              {isDesktopUser ? 'Order Summary' : 'Total'}
            </Heading>
            <Tooltip
              ariaId="checkout-tooltip"
              triggerButtonClass="align-sub btnTextPrimary"
              centered={!showItemizedCheckout}
              clamp={!isDesktopUser ? { enabled: true, transform: 'translate(0, 50px)' } : null}
              position={!isDesktopUser ? 'mobileOrderSummary' : 'bottom'}
              tooltipClass={!isDesktopUser ? 'ml-0' : ''}
              trackArrow
            >
              <Text className="text-xs">
                Total shown is based on your box size and the price per serving. You can make
                changes to your meal selections until {basketCutoffTime(box?.editingCutoff)} on{' '}
                {basketCutoffDate(box?.editingCutoff)} final total will reflect any changes, as
                applicable.
              </Text>
            </Tooltip>
          </div>
          {!isDesktopUser && (
            <>
              {user?.smsDiscount && (
                <DiscountTag
                  discountText="5%"
                  brandStylesOverrides="text-secondary"
                  spanClassNames="flex-none"
                />
              )}
              {box?.promoApplied && box?.subtotalFormatted !== box?.promo.subtotalFormatted && (
                <DiscountTag
                  discountText={box.promo.displayedDiscount}
                  spanClassNames="flex-none"
                />
              )}
              <button
                className="flex cursor-pointer border-none bg-transparent"
                onClick={() => setShowItemizedCheckout(!showItemizedCheckout)}
                type="button"
              >
                <Text
                  brandStylesOverrides={isDesktopUser ? 'h4' : 'textMd'}
                  className="mb-0 mr-1"
                  htmlElement="span"
                >
                  {box?.totalFormatted}
                </Text>
                <CaretDown
                  className={cn('h-4 w-4 align-top transition-all', {
                    ['rotate-180 transform']: showItemizedCheckout,
                  })}
                />
              </button>
            </>
          )}
        </div>
        {!isDesktopUser && <AdditionalOrderInformation />}
        <CSSTransition
          in={showItemizedCheckout || isDesktopUser}
          timeout={500}
          classNames={{
            enter: 'fadeInOut-enter scaleHeight-enter',
            enterActive: 'fadeInOut-enter-active scaleHeight-enter-active',
            exit: 'fadeInOut-exit scaleHeight-exit',
            exitActive: 'fadeInOut-exit-active scaleHeight-exit-active',
          }}
          unmountOnExit
          nodeRef={transitionRef}
        >
          <dl ref={transitionRef}>
            {isHomeChef && (
              <div className="mt-4 flex bp3:mt-0">
                <Text htmlElement="dt" className="mb-2 flex-1 font-normal">
                  Servings Per Meal
                </Text>
                <Text htmlElement="dd">{displayServings}</Text>
              </div>
            )}
            <div className={cn('flex', { 'mt-4 bp3:mt-0': isTempo })}>
              <Text htmlElement="dt" className={cn('mb-3 flex-1', { 'font-normal': isHomeChef })}>
                Meals Per Week
              </Text>
              <Text htmlElement="dd">{user.weeklyMeals}</Text>
            </div>
            <Text brandStylesOverrides="textSm">You can change this at any time</Text>
            <hr className="rule my-4 bg-charcoal-60" />
            {box?.creditAppliedCents > 0 ? <GiftCardSummary /> : <FullPriceOrVoucherSummary />}
            <div className="flex pb-2">
              <Text htmlElement="dt" className="flex-1 font-normal" brandStylesOverrides="textMd">
                Total
              </Text>
              <Text
                htmlElement="dd"
                brandStylesOverrides="textMd text-secondary"
                className="font-normal"
              >
                {box?.totalFormatted}
              </Text>
            </div>
            {voucher && voucher.freeForLifeDiscounts && voucher.freeForLifeDiscounts.length > 0 && (
              <div className="my-4 flex flex-col gap-2">
                {voucher.freeForLifeDiscounts.map((discount, index) => {
                  return <FreeForLifeBanner text={discount.bannerText} key={index} />;
                })}
              </div>
            )}

            {box?.creditRemainingCents > 0 && (
              <div
                className={cn('mt-2 rounded border px-4 py-2', {
                  'border-green-100 bg-green-50': isHomeChef,
                  'border-tempo-blue-120 bg-tempo-blue-50': !isHomeChef,
                })}
              >
                <Text brandStylesOverrides="textMd text-primary-dark" className="mb-0">
                  Your remaining gift card balance of {box?.creditRemainingFormatted} will be
                  applied to future orders.
                </Text>
              </div>
            )}
            {isDesktopUser && <AdditionalOrderInformation />}
          </dl>
        </CSSTransition>
      </section>
      <PromoCodesAndGiftCards />
    </div>
  );
};

OrderSummary.propTypes = {
  user: PropTypes.object,
  setShowSlideout: PropTypes.func,
  voucher: PropTypes.object,
  box: PropTypes.shape({
    subtotalFormatted: PropTypes.string.isRequired,
    subtotalCents: PropTypes.number.isRequired,
    totalFormatted: PropTypes.string.isRequired,
    totalCents: PropTypes.number.isRequired,
    promoApplied: PropTypes.bool.isRequired,
    shippingPromoApplied: PropTypes.bool,
    shippingFormatted: PropTypes.string.isRequired,
    shippingCents: PropTypes.number.isRequired,
    taxFormatted: PropTypes.string.isRequired,
    taxCents: PropTypes.number.isRequired,
    pricePerServingFormatted: PropTypes.string.isRequired,
    pricePerServingCents: PropTypes.number.isRequired,
    afterSignUpTotalFormatted: PropTypes.string.isRequired,
    afterSignUpTotalCents: PropTypes.number.isRequired,
    creditAppliedCents: PropTypes.number.isRequired,
    creditAppliedFormatted: PropTypes.string.isRequired,
    creditRemainingCents: PropTypes.number.isRequired,
    creditRemainingFormatted: PropTypes.string.isRequired,
    promo: PropTypes.shape({
      subtotalCents: PropTypes.number,
      subtotalFormatted: PropTypes.string,
      shippingPromo: PropTypes.object,
      maxDiscountMessage: PropTypes.string,
      displayedDiscount: PropTypes.string,
    }),
    smsIncentivePromo: PropTypes.shape({
      subtotalCents: PropTypes.number,
      subtotalFormatted: PropTypes.string,
    }),
    editingCutoff: PropTypes.string.isRequired,
  }),
  text: PropTypes.string,
  className: PropTypes.string,
};

export default OrderSummary;
